import { Box } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
    useStableCallback,
    type FormOnSubmitFailedFn,
    type FormOnSubmitNotAllowedFn,
    type PropertyPath
} from "@ngt-form/core";
import type { ReactNode } from "react";

import AppSettings from "src/AppSettings";
import type { AppIconDefinition } from "src/components/common/Icon";
import Icon from "src/components/common/Icon";
import type { TMDValidationError } from "src/types/TMDValidationError";

/**
 * Supplies a stable failed-submit callback with optional notification presentation overrides.
 *
 * Postconditions: Creating the callback causes no notification or save. Falsy icon/message values
 * retain the default critical-error icon and message when the callback is invoked.
 */
const useTMDFormOnSubmitFailed = (
    icon?: AppIconDefinition | null | undefined,
    message?: ReactNode | null | undefined
): FormOnSubmitFailedFn<any, TMDValidationError, any> => {
    /**
     * Requests an error notification and returns only errors attributed to ngt-form-submit.
     *
     * Postconditions: On fulfillment, returns existing error-array references or undefined when that
     * source has no errors. The supplied failure argument is ignored; no rollback or retry occurs.
     * The notification does not independently establish whether a preceding save persisted data.
     */
    const onSubmitNotAllowed: FormOnSubmitFailedFn<any, TMDValidationError, any> =
        useStableCallback(async (snapshot, _error, _formManager) => {
            notifications.show({
                color: "error",
                title: "Error",
                message: (
                    <Box>
                        {message ? (
                            message
                        ) : (
                            <>
                                The form was <strong> not </strong> saved.
                            </>
                        )}
                    </Box>
                ),
                icon: <Icon icon={icon ? icon : AppSettings.icons.error.critical} />
            });

            const formErrors = snapshot.formErrorsBySource["ngt-form-submit"] ?? [];
            const fieldErrors: Record<PropertyPath, TMDValidationError[]> = {};

            let hasErrors = formErrors?.length !== 0;

            for (let propertyPath of Object.keys(snapshot.fieldErrorsBySource)) {
                const propertyPathErrors =
                    snapshot.fieldErrorsBySource[propertyPath]["ngt-form-submit"];

                if (propertyPathErrors && propertyPathErrors.length !== 0) {
                    hasErrors = true;
                    fieldErrors[propertyPath] = propertyPathErrors;
                }
            }

            if (hasErrors) {
                return {
                    form: formErrors,
                    fields: fieldErrors
                };
            }

            return undefined;
        });

    return onSubmitNotAllowed;
};

export default useTMDFormOnSubmitFailed;
